import {
    IContainer,
    Page,
    EntityFactory,
    ContainerRef,
} from "@amzn/ask-legal-domain";
import { Button, Spinner, Toggle } from "@amzn/awsui-components-react";
import * as React from "react";
import { useLatestRequest } from "../../hooks/use-latest-request";
import { callApi, Loadable } from "../../utils/api-call";
import { AppContext } from "../../setup/context";
import { FavorButtonV2 } from "../common/FavorButtonV2";
import { UIModel } from "../../model/ui-model";
import { ViewContainerContent } from "./modal-content/ViewContainerContent";
import { ContainerDiffView } from "./ContainerDiffView";
import { getQueryParameterMap, isCompareFeatureActivated, scrollWithDelayInMs } from "../../utils/common-utils";
import { AppURLFactory } from "../../factory/app-url-factory";
import { BackToTopButtonV2, ContainerCardV2, ContainerLinkCopyButtonV2 } from "./ContainerCardV2";

/**
 * Card for one container in read-only views (PathFinder Agentic UI lab): the live page and
 * the draft review view. Expand/collapse is fully controlled by the page (`isExpanded` +
 * `pageControlDispatch`). The content keeps the `.custom-container` wrapper and `<a id>` anchor
 * that TextHighlightShare and `?container=` deep links rely on.
 */
export const ContainerViewV2 = (props: ContainerViewV2Props) => {
    const context = React.useContext(AppContext);
    const [loadState, setLoadState] = React.useState<Loadable<IContainer.LoadContainerOutput>>({ status: "loading" });
    const [isLoadingLiveVersion, setIsLoadingLiveVersion] = React.useState<boolean>(false);
    const [showDiff, setShowDiff] = React.useState<boolean>(false);
    const [liveContainerData, setLiveContainerData] = React.useState<IContainer.LoadContainerOutput>();
    // Only the latest load for this container may update state (id/version can change while loading)
    const loadRequest = useLatestRequest();
    const bodyId = `pf-card-body-${props.containerRef.id}`;

    const isFavoritedState = UIModel.State.use<boolean>({
        initialValue: false
    });

    React.useEffect(() => {
        const isCurrent = loadRequest.begin();
        const containerAPI = context.getContainerAPI();
        setLoadState({ status: "loading" });
        setLiveContainerData(undefined);
        setIsLoadingLiveVersion(false);
        setShowDiff(false);
        callApi(containerAPI.loadVersion, props.containerRef).then(result => {
            if (!isCurrent()) return;
            if (result.ok) {
                isFavoritedState.setValue(result.data.isFavorited);
                setLoadState({ status: "loaded", data: result.data });
            } else {
                setLoadState({ status: "error", err: result.err });
            }
        });
        if (
            !props.showCompare ||
            !props.liveContainerRef ||
            props.liveContainerRef.version === props.containerRef.version ||
            !isCompareFeatureActivated(props.liveContainerRef.containerType)
        ) return;
        setIsLoadingLiveVersion(true);
        callApi(containerAPI.loadVersion, props.liveContainerRef).then(result => {
            if (!isCurrent()) return;
            setIsLoadingLiveVersion(false);
            if (result.ok) {
                setLiveContainerData(result.data);
            }
        });
    }, [
        props.containerRef.id,
        props.containerRef.version,
        props.liveContainerRef?.id,
        props.liveContainerRef?.version,
        props.showCompare
    ]);

    // Runs after the loaded content is rendered, so the deep-link target element exists
    React.useEffect(() => {
        if (loadState.status !== "loaded") return;
        !!props.onLoadComplete && props.onLoadComplete(loadState.data);
        const queryParamMap = getQueryParameterMap();
        if (queryParamMap.get("container") === props.containerRef.id) {
            // Skip container scroll when deep-linking to a specific question or highlight —
            // those handlers manage their own scroll behavior
            if (queryParamMap.get("question") || queryParamMap.get("highlight")) return;
            const targetContainerId = queryParamMap.get("container");
            scrollWithDelayInMs(700, targetContainerId);
        }
    }, [loadState]);

    const toggleExpanded = () => {
        props.pageControlDispatch({
            type: "toggle",
            index: props.containerIndex
        });
    };

    if (loadState.status === "error") {
        return (
            <ContainerCardV2 title="Error Loading Container" bodyId={bodyId}>
                Error Loading Container
            </ContainerCardV2>
        );
    }

    if (loadState.status !== "loaded") {
        return (
            <ContainerCardV2 title="Loading..." bodyId={bodyId}>
                <Spinner />
            </ContainerCardV2>
        );
    }

    const loadedContainer = loadState.data;
    const containerMetadata = loadedContainer.containerMetadata;
    const canCompare = !!liveContainerData ||
        (!!props.showCompare && !props.liveContainerRef && isCompareFeatureActivated(props.containerRef.containerType));

    return (
        <ContainerCardV2
            title={containerMetadata.title}
            bodyId={bodyId}
            expanded={props.isExpanded}
            onToggle={toggleExpanded}
            bodyClassName="custom-container"
            headerExtras={props.showFavoriteButton && (
                <FavorButtonV2
                    entityLabel="container"
                    isFavoritedState={isFavoritedState}
                    targetEntityRef={EntityFactory.toEntityRef(containerMetadata)}
                />
            )}
            actions={<>
                {canCompare && (
                    <Toggle onChange={({ detail }) => setShowDiff(detail.checked)} checked={showDiff}>
                        Compare with live
                    </Toggle>
                )}
                {!liveContainerData && isLoadingLiveVersion && <Spinner />}
                {!!props.showEditButton && (
                    <Button
                        variant="icon"
                        iconName="edit"
                        ariaLabel="Edit container"
                        href={AppURLFactory.getContainer(props.containerRef.id, {
                            type: "draft",
                            params: new Map([["edit", "true"]])
                        })}
                        target="_blank"
                    />
                )}
                <ContainerLinkCopyButtonV2 containerId={props.containerRef.id} />
                {props.location === "Main" && <BackToTopButtonV2 />}
            </>}
        >
            <a id={containerMetadata.id}>
                {!showDiff && (
                    <ViewContainerContent
                        containerMetadata={containerMetadata}
                        content={loadedContainer.loadedContent}
                        pageSection={props.location}
                        expandCollapseAllTrigger={props.expandCollapseAllTrigger}
                        showEditControls={props.showEditButton}
                    />
                )}
                {showDiff && (
                    <ContainerDiffView
                        newContainer={loadedContainer}
                        oldContainer={liveContainerData}
                    />
                )}
            </a>
        </ContainerCardV2>
    );
};

export interface ContainerViewV2Props {
    containerRef: ContainerRef;
    location?: Page.SectionLocation;
    isExpanded: boolean;
    containerIndex: number;
    /**
     * If set to false, this container cannot be favorited, and favor button is hidden
     */
    showFavoriteButton?: boolean;
    /**
     * If set to true, shows the compare-with-live toggle (draft review view)
     */
    showCompare?: boolean;
    /**
     * If set to true, show edit button that will navigate to draft page
     */
    showEditButton?: boolean;
    /**
     * Live container reference for computing diff
     */
    liveContainerRef?: ContainerRef;
    onLoadComplete?: (loadedData: IContainer.LoadContainerOutput) => void;
    pageControlDispatch: (params: { type: "expand-all" | "collapse-all" | "toggle", index: number }) => void;
    expandCollapseAllTrigger?: { value: string; };
}
